// src/pages/Profile.js
import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential, sendEmailVerification, sendPasswordResetEmail } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth } from '../services/firebase';

export default function Profile() {
  const { currentUser } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('info');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    displayName: currentUser?.displayName || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [profilePic, setProfilePic] = useState(currentUser?.photoURL || null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Upload image to Firebase Storage and get download URL
  const uploadImageToStorage = async (imageFile) => {
    const storage = getStorage();
    const storageRef = ref(storage, `profilePics/${currentUser.uid}`);
    
    // Upload the file
    await uploadBytes(storageRef, imageFile);
    
    // Get download URL
    const url = await getDownloadURL(storageRef);
    return url;
  };

  // Handle profile picture upload
  const handleProfilePicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      showMessage('Please select a valid image file', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      showMessage('Image size should be less than 5MB', 'error');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Upload to Firebase Storage and get URL
      const photoURL = await uploadImageToStorage(file);
      
      // Update user profile with new photo URL
      await updateProfile(currentUser, { photoURL });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setProfilePic(photoURL);
      
      showMessage('Profile picture updated successfully!');
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      showMessage('Error uploading profile picture: ' + error.message, 'error');
    } finally {
      setLoading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  // Update user profile (display name)
  const handleUpdateProfile = async () => {
    if (!formData.displayName.trim()) {
      showMessage('Display name cannot be empty', 'error');
      return;
    }

    setLoading(true);
    try {
      await updateProfile(currentUser, {
        displayName: formData.displayName
      });
      showMessage('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      showMessage('Error updating profile: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Change password with reauthentication
  const handleChangePassword = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      showMessage('New passwords do not match', 'error');
      return;
    }

    if (formData.newPassword.length < 6) {
      showMessage('Password must be at least 6 characters', 'error');
      return;
    }

    if (!formData.currentPassword) {
      showMessage('Please enter your current password', 'error');
      return;
    }

    setLoading(true);
    try {
      // Re-authenticate user before password change :cite[1]
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        formData.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      
      // Change password
      await updatePassword(currentUser, formData.newPassword);
      
      showMessage('Password changed successfully!');
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        showMessage('Current password is incorrect', 'error');
      } else if (error.code === 'auth/requires-recent-login') {
        showMessage('Please re-authenticate to change your password', 'error');
      } else {
        showMessage('Error changing password: ' + error.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Send email verification
  const handleSendVerification = async () => {
    setLoading(true);
    try {
      await sendEmailVerification(currentUser);
      showMessage('Verification email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error sending verification:', error);
      showMessage('Error sending verification: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Send password reset email
  const handleResetPassword = async () => {
    if (!currentUser?.email) {
      showMessage('No email address found', 'error');
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      showMessage('Password reset email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error sending reset email:', error);
      showMessage('Error sending reset email: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-semibold text-gray-900">Profile</h1>
          </div>
          
          {/* Navigation Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveSection('info')}
                className={`mr-8 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'info'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Account Information
              </button>
              <button
                onClick={() => setActiveSection('password')}
                className={`mr-8 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'password'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Change Password
              </button>
              <button
                onClick={() => setActiveSection('photo')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'photo'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Profile Photo
              </button>
            </nav>
          </div>

          <div className="p-6">
            {message && (
              <div className={`mb-6 p-4 rounded ${
                messageType === 'success' 
                  ? 'bg-green-100 border border-green-400 text-green-700' 
                  : 'bg-red-100 border border-red-400 text-red-700'
              }`}>
                {message}
              </div>
            )}

            {/* Account Information Section */}
            {activeSection === 'info' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Display Name
                      </label>
                      <input
                        type="text"
                        name="displayName"
                        value={formData.displayName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter display name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email Address</label>
                      <p className="mt-1 text-sm text-gray-900">{currentUser?.email}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">User ID</label>
                      <p className="mt-1 text-sm text-gray-900 font-mono">{currentUser?.uid}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email Verified</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {currentUser?.emailVerified ? (
                          <span className="text-green-600 font-semibold">Yes</span>
                        ) : (
                          <span className="text-red-600 font-semibold">No</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    <button
                      onClick={handleUpdateProfile}
                      disabled={loading}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Updating...' : 'Update Profile'}
                    </button>
                    
                    <button
                      onClick={handleSendVerification}
                      disabled={loading || currentUser?.emailVerified}
                      className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {currentUser?.emailVerified ? 'Email Verified' : 'Send Verification Email'}
                    </button>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
                  
                  <div className="space-y-3">
                    <button
                      onClick={handleResetPassword}
                      disabled={loading}
                      className="w-full bg-orange-600 text-white py-2 px-4 rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Send Password Reset Email
                    </button>
                    
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <h4 className="text-sm font-medium text-yellow-800">Security Note</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Changing sensitive information may require re-authentication for security purposes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Change Password Section */}
            {activeSection === 'password' && (
              <div className="max-w-md">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter current password"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter new password"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Confirm new password"
                    />
                  </div>
                  
                  <button
                    onClick={handleChangePassword}
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Changing Password...' : 'Change Password'}
                  </button>
                </div>
              </div>
            )}

            {/* Profile Photo Section */}
            {activeSection === 'photo' && (
              <div className="max-w-md">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Photo</h3>
                
                <div className="flex items-center space-x-6 mb-6">
                  <div className="relative">
                    <img
                      className="w-24 h-24 rounded-full object-cover border-2 border-gray-300"
                      src={profilePic || `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.displayName || currentUser?.email || 'User')}&background=0D8ABC&color=fff`}
                      alt="Profile"
                    />
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm">{uploadProgress}%</span>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleProfilePicUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                      className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Uploading...' : 'Upload New Photo'}
                    </button>
                    <p className="text-sm text-gray-500 mt-2">
                      JPG, PNG or GIF (max 5MB)
                    </p>
                  </div>
                </div>
                
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <h4 className="text-sm font-medium text-blue-800">Tips</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    For best results, use a square image that's at least 200x200 pixels.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}